// relativeTime.ts
import {
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInMonths,
  differenceInYears,
} from 'date-fns';

/**
 * Returns a localized relative time string, e.g. "3 days ago" or "in 2 hours".
 * Uses date-fns for arithmetic, Intl.RelativeTimeFormat for localization.
 *
 * Returns a "just-now" key when < 1 minute difference -- because Intl.RelativeTimeFormat only
 * handles quantified time units this case needs to be translated separately.
 *
 * @example
 * formatRelativeTime(new Date(Date.now() - 5 * 60 * 1000), 'en'); // "5 minutes ago"
 * formatRelativeTime(new Date(Date.now() + 2 * 60 * 60 * 1000), 'fr'); // "dans 2 heures"
 * formatRelativeTime(new Date(), 'de'); // "just-now"
 */
export function formatRelativeTime(iso: Date | string, locale: string) {
  const target = typeof iso === 'string' ? new Date(iso) : iso;
  const now = new Date();

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  const diffInMinutes = differenceInMinutes(target, now);
  if (Math.abs(diffInMinutes) < 1) {
    return 'just-now';
  }

  if (Math.abs(diffInMinutes) < 60) {
    return rtf.format(diffInMinutes, 'minute');
  }

  const diffInHours = differenceInHours(target, now);
  if (Math.abs(diffInHours) < 24) {
    return rtf.format(diffInHours, 'hour');
  }

  const diffInDays = differenceInDays(target, now);
  if (Math.abs(diffInDays) < 30) {
    return rtf.format(diffInDays, 'day');
  }

  const diffInMonths = differenceInMonths(target, now);
  if (Math.abs(diffInMonths) < 12) {
    return rtf.format(diffInMonths, 'month');
  }

  return rtf.format(differenceInYears(target, now), 'year');
}

/**
 * Formats an ISO date string or Date object into an absolute date string
 * localized to the specified locale using Intl.DateTimeFormat.
 *
 * @example
 * formatAbsoluteTime(new Date('2023-01-15T10:30:00Z'), 'en'); // "January 15, 2023"
 * formatAbsoluteTime('2023-01-15T10:30:00Z', 'fr'); // "15 janvier 2023"
 * formatAbsoluteTime(new Date(), 'de'); // "20. Juni 2024"
 */
export function formatAbsoluteTime(iso: Date | string, locale: string, options?: Intl.DateTimeFormatOptions) {
  const target = typeof iso === 'string' ? new Date(iso) : iso;
  return Intl.DateTimeFormat(locale, {
    dateStyle: 'long',
    ...options,
  }).format(target);
}
